import toast, { Toast, ToastOptions } from "react-hot-toast";
import { createTranslator } from "@/i18n";
import { savedSLanguage } from "@/pages/SEmbed/useSEmbed";

type ToastType = "success" | "error";
type ToastPreset = "default" | "bottom";

// Colours come from the S tokens, so toasts follow the theme.
const bottomToastOptions: ToastOptions = {
  position: "bottom-center",
  duration: 3000,
  style: {
    background: "var(--success)",
    color: "var(--background)",
    border: "none",
    padding: "16px",
    borderRadius: "calc(var(--radius) * 0.8)",
    // One line on wide screens, wrapping within a 16px gutter on phones.
    maxWidth: "calc(100vw - 32px)",
    fontSize: "14px",
    fontWeight: "var(--weight-label)",
  },
};

const createToastContent = (message: string, t: Toast) => {
  const { dir, t: translate } = createTranslator(savedSLanguage());
  return (
    <div dir={dir} className="flex items-center gap-2.5">
      <button
        type="button"
        aria-label={translate("common.dismiss")}
        onClick={() => toast.dismiss(t.id)}
        className="cursor-pointer border-none bg-transparent text-base text-current"
      >
        ✕
      </button>
      <span>{message}</span>
    </div>
  );
};

export const customToast = (
  message: string,
  type: ToastType,
  preset: ToastPreset = "default",
  optionsOverrides: ToastOptions = {},
) => {
  if (preset === "bottom") {
    const options = {
      ...bottomToastOptions,
      ...optionsOverrides,
      style: {
        ...bottomToastOptions.style,
        background: type === "error" ? "var(--destructive)" : "var(--success)",
        ...optionsOverrides.style,
      },
    };
    toast((t) => createToastContent(message, t), options);
  } else {
    // Toasts can fire before a page sets its direction, so they carry the language's own.
    const { dir } = createTranslator(savedSLanguage());
    toast[type](message, {
      ...optionsOverrides,
      ariaProps: { role: "status", "aria-live": "polite", ...optionsOverrides.ariaProps },
      style: { direction: dir, ...optionsOverrides.style },
    });
  }
};
